import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';

const BoreholeList = () => {
  const navigate = useNavigate();
  const [boreholes, setBoreholes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBoreholes();
  }, []);

  const fetchBoreholes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/boreholes/');
      if (response.data.data) {
        setBoreholes(response.data.data);
      }
    } catch (error) {
      console.error('시추공 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">시추공 목록</h1>
          <p className="text-secondary-600">전체 시추공 데이터를 확인하고 관리합니다</p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-soft">
            <p className="text-xs text-secondary-600 mb-1">전체 시추공</p>
            <p className="text-2xl font-bold text-secondary-900">{boreholes.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-soft">
            <p className="text-xs text-secondary-600 mb-1">지반 분석 완료</p>
            <p className="text-2xl font-bold text-blue-600">
              {boreholes.filter(b => b.soil_type).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-soft">
            <p className="text-xs text-secondary-600 mb-1">액상화 안전</p>
            <p className="text-2xl font-bold text-green-600">
              {boreholes.filter(b => b.liquefaction_assessment === '안전').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-soft">
            <p className="text-xs text-secondary-600 mb-1">액상화 평가 필요</p>
            <p className="text-2xl font-bold text-yellow-600">
              {boreholes.filter(b => b.liquefaction_assessment === '평가 필요').length}
            </p>
          </div>
        </div>

        {/* 테이블 */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {boreholes.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-secondary-600 mb-4">등록된 시추공이 없습니다</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      이름
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      시추주상도
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      좌표 (X, Y)
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      지하수위
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      지반 종류
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                      액상화 평가
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {boreholes.map((borehole) => (
                    <tr
                      key={borehole.id}
                      onClick={() => navigate(`/boreholes/${borehole.id}`)}
                      className="hover:bg-secondary-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        #{borehole.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-secondary-900">{borehole.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {borehole.drill_log?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {borehole.x?.toFixed(2)}, {borehole.y?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                        {borehole.ground_water_table ? `${borehole.ground_water_table.toFixed(2)}m` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                          borehole.soil_type
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}>
                          {borehole.soil_type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                          borehole.liquefaction_assessment === '안전'
                            ? 'bg-green-100 text-green-700'
                            : borehole.liquefaction_assessment === '평가 필요'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}>
                          {borehole.liquefaction_assessment || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoreholeList;
